import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribes to a CSS media query and returns whether it currently
 * matches. Implemented on top of `useSyncExternalStore` so React gets
 * a single, consistent snapshot per render — no setState-in-effect
 * cascade — and so SSR can opt into a deterministic default.
 *
 * Callers should pass the same string Tailwind would emit, e.g.
 * `(min-width: 48rem)` for the `md:` breakpoint. The Aurora `@theme`
 * block in `index.css` publishes the breakpoint tokens so JS and CSS
 * agree on the numbers.
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {}
      }
      const list = window.matchMedia(query)
      list.addEventListener('change', notify)
      return () => list.removeEventListener('change', notify)
    },
    [query],
  )

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return defaultValue
    }
    return window.matchMedia(query).matches
  }, [query, defaultValue])

  const getServerSnapshot = useCallback(() => defaultValue, [defaultValue])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * Aurora's "≥ md" breakpoint — matches the Tailwind `md:` prefix.
 * Centralised so responsive logic agrees with the CSS layer.
 */
export const MD_QUERY = '(min-width: 48rem)'

/** Convenience: `true` once the viewport is at least the md breakpoint. */
export function useIsDesktop(): boolean {
  return useMediaQuery(MD_QUERY)
}
